import { isValidString } from "@/Utils/String";

export enum DEBUG_LEVEL {
  "NONE" = "NONE",
  "INFO" = "INFO",
  "WARN" = "WARN",
  "DANGER" = "DANGER",
}

type appMode = undefined | "app" | "ios" | "android";

const getInt = (val: string | undefined | null, defValue: number): number => {
  if (!isValidString(val)) {
    return defValue;
  }

  try {
    const intCandidate = parseInt(val, 10);

    if (Number.isNaN(intCandidate)) {
      return defValue;
    }

    return intCandidate;
  } catch (e) {
    return defValue;
  }
};

const DEBUG_LEVELS: string[] = [DEBUG_LEVEL.INFO, DEBUG_LEVEL.WARN, DEBUG_LEVEL.DANGER];
const level = process.env.DEBUG_LEVEL;
const forceAppMode: appMode = process.env.FORCE_APP_MODE as appMode;
const breakpointDesktop = getInt(process.env.BREAKPOINT_DESKTOP, 720);
const privacyCodeLength = getInt(process.env.PRIVACY_CODE_LENGTH, 4);
const privacyValidSecsCode = getInt(process.env.PRIVACY_VALID_SECS_CODE, 30 * 60);

let debugLevel = DEBUG_LEVEL.NONE;
let checkConnetionResourceUrl: string | undefined = undefined;
let checkConnectionSecsTimeout = 2;
let onboardingVersion = 0;

if (isValidString(level) && DEBUG_LEVELS.includes(level.toUpperCase())) {
  debugLevel = level.toUpperCase() as DEBUG_LEVEL;
}

const redirectToChat =
  isValidString(process.env.REDIRECT_TO_CHAT) && process.env.REDIRECT_TO_CHAT.toLowerCase() === "false"
    ? false
    : true;

try {
  if (isValidString(process.env.CHECK_CONNECTION_RESOURCE_URL)) {
    checkConnetionResourceUrl = process.env.CHECK_CONNECTION_RESOURCE_URL;
  }

  if (isValidString(process.env.CHECK_CONNECTION_SECS_TIMEOUT)) {
    checkConnectionSecsTimeout = getInt(process.env.CHECK_CONNECTION_SECS_TIMEOUT, 2);
  }
} catch (e) {
  checkConnetionResourceUrl = undefined;
  checkConnectionSecsTimeout = 2;
}

if (isValidString(process.env.ONBOARDING_VERSION)) {
  onboardingVersion = getInt(process.env.ONBOARDING_VERSION, 0);
}

// type definition in src/env.d.ts check https://vitejs.dev/guide/env-and-mode.html#intellisense-for-typescript
export const config = {
  apiUrl: process.env.API_URL as string,
  appUrl: process.env.APP_URL as string,
  webVersion: process.env.WEB_VERSION,
  webSocketUrl: process.env.WEB_SOCKET_URL,
  webSocketPing: getInt(process.env.WEB_SOCKET_PING, 60000),
  stripeKey: process.env.STRIPE_KEY as string,
  supportEmail: process.env.SUPPORT_EMAIL,
  mode: process.env.MODE,
  minPageLoadingSeconds: getInt(process.env.PAGE_LOADING_MIN_SECONDS, 3),
  unsubscribeForms: {
    es: process.env.UNSUBSCRIBE_FORM_ES as string,
    en: process.env.UNSUBSCRIBE_FORM_EN as string,
    it: process.env.UNSUBSCRIBE_FORM_IT as string,
  },
  deleteAccountForms: {
    es: process.env.DELETE_ACCOUNT_FORM_ES as string,
    en: process.env.DELETE_ACCOUNT_FORM_EN as string,
    it: process.env.DELETE_ACCOUNT_FORM_IT as string,
  },
  reassignForms: {
    es: process.env.REASSIGN_FORM_ES as string,
    en: process.env.REASSIGN_FORM_EN as string,
    it: process.env.REASSIGN_FORM_IT as string,
  },
  debugLevel,
  breakpointDesktop,
  gtmAuth: process.env.GTM_AUTH,
  gtmPreview: process.env.GTM_PREVIEW,
  googleTagManagerId: process.env.GOOGLE_TAG_MANAGER_ID,
  appsFlyerId: process.env.ANALYTICS_APPS_FLYER,
  analyticsQuestionEmailId: process.env.API_EMAIL_QUESTION_ID,
  hubspotCode: process.env.HUBSPOT_CODE,
  productionUrl: process.env.PRODUCTION_URL,
  redirectToChat,
  testCookies: Boolean(process.env.TEST_COOKIES),
  sentryDsn: process.env.SENTRY_DSN,
  sentryEnv: `${process.env.SENTRY_ENV}`,
  privacyCodeLength,
  privacyValidSecsCode,
  forceAppMode,
  iosAppVersion: process.env.IOS_APP_VERSION,
  androidAppVersion: process.env.ANDROID_APP_VERSION,
  androidAppTimestamp: process.env.ANDROID_APP_TIMESTAMP,
  checkConnetionResourceUrl,
  checkConnectionSecsTimeout,
  vonageApiKey: process.env.VONAGE_API_KEY,
  trackersDisabled: isValidString(process.env.TRACKERS_DISABLED)
    ? process.env.TRACKERS_DISABLED === "true"
    : false,
  onboardingVersion,
  isEnabledTracking: process.env.TRACKING !== "false",
  timeZoneValidMs: process.env.TIME_ZONE_VALID_MS,
  defaultMinSessionDuration: getInt(process.env.DEFAULT_MIN_SESSION_DURATION, 50),
};
